import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Constants, { USER_ROLE } from "constants/index";
import Screens from "constants/screens";
import Strings from "constants/strings";
import UserService from "services/identity/user.service";
import ReportService from "services/medkit/report.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { setDataAlert, showLoading } from "store/slice/common.slice";
import { ActionRequest, BooleanInt, Mode, RepeatIssue, ReportDocumentType, ReportStatus } from "constants/enum";
import { IDataDetailAnalysis, IErrorAnalysis, IFileUploaderRes, IReportDocument, IRequestReportAnalyze } from "commons/interfaces";

import CardCause from "./CardCause";
import CardByHospital from "./CardByHospital";
import CardImageAnalysis from "./CardImageAnalysis";
import CardIncidentGroup from "./CardIncidentGroup";
import CardSeverityOfInjury from "./CardSeverityOfInjury";
import CardExpertEvaluation from "./CardExpertEvaluation";
import CardGeneralInformation from "./CardGeneralInformation";
import CardImageFishboneDiagrams from "./CardImageFishboneDiagrams";
import CardIncidentResolutionMonitoring from "./CardIncidentResolutionMonitoring";



const userService = new UserService();
const reportService = new ReportService();

const ReportAnalysisScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { userInfo } = useCommonComponentContext();
    const dataMapCodenames = useSelector((state: RootState) => state.common.codenames);
    const listPathName = useSelector((state: RootState) => state.titleRoute.listPathName);

    const [mode, setMode] = useState(Mode.View);
    const [userRole, setUserRole] = useState("");
    const [hasUpdated, setHasUpdated] = useState(false);
    const [errorAnalysis, setErrorAnalysis] = useState<IErrorAnalysis>({});
    const [dataAnalysis, setDataAnalysis] = useState<IDataDetailAnalysis>({});
    const [dataDocumentsBackup, setDataDocumentsBackup] = useState<IReportDocument[]>([]);
    const [dataMapReportDocumentTypeAnalyze, setDataMapReportDocumentTypeAnalyze] = useState<Map<string, IReportDocument>>(new Map());
    const [dataMapReportDocumentTypeFishboneDiagram, setDataMapReportDocumentTypeFishboneDiagram] = useState<Map<string, IReportDocument>>(new Map());

    const reportId = searchParams.get("id");

    useEffect(() => {

        (async () => {
            try {
                dispatch(showLoading(true));

                setHasUpdated(false);
                setErrorAnalysis({});

                if (userInfo?.userProfile?.roleCode) {
                    setUserRole(userInfo?.userProfile?.roleCode);
                };

                if (!Helpers.isNullOrEmpty(reportId)) {

                    const result = await reportService.detail(reportId);

                    let userIds: string[] = [];
                    const newDataMapUser: Map<string, string> = new Map();
                    if (!Helpers.isNullOrEmpty(result?.inchargedStaff)) {
                        userIds.push(result?.inchargedStaff);
                    };
                    if (!Helpers.isNullOrEmpty(result?.coordinatedStaff)) {
                        userIds.push(result?.coordinatedStaff);
                    };
                    userIds = Helpers.uniqueArray(userIds);
                    if (userIds.length > 0) {
                        const resultUser = await userService.getPaged({
                            selectedIds: userIds,
                            nonHierarchical: BooleanInt.False,
                            organizationId: userInfo?.userProfile?.organizationId,
                        });
                        for (const element of [...resultUser?.selectedItems || []]) {
                            let fullName = element.fullName;
                            if (element.organizationUserProfile?.lastName || element.organizationUserProfile?.firstName) {
                                fullName = `${element.organizationUserProfile?.lastName || ""} ${element.organizationUserProfile?.firstName || ""}`.trim();
                            };
                            if (element.id === result?.inchargedStaff) {
                                newDataMapUser.set(element.id, fullName);
                            };
                            if (element.id === result?.coordinatedStaff) {
                                newDataMapUser.set(element.id, fullName);
                            }
                        };
                    };

                    const fileAnalyzeUrls: IFileUploaderRes[] = [];
                    const fileFishboneDiagramUrls: IFileUploaderRes[] = [];
                    const documentBackup: IReportDocument[] = [];
                    const dataMapDocumentTypeAnalyze: Map<string, IReportDocument> = new Map();
                    const dataMapDocumentTypeFishboneDiagram: Map<string, IReportDocument> = new Map();
                    if (!Helpers.isNullOrEmpty(result.reportDocuments)) {
                        for (const element of [...result.reportDocuments || []]) {
                            if ([ReportDocumentType.ImgAnalyze, ReportDocumentType.ImgFishboneDiagram].includes(element.type)) {
                                if (ReportDocumentType.ImgAnalyze === element.type) {
                                    fileAnalyzeUrls.push({
                                        id: element.id,
                                        fileId: element.documentId,
                                        fileAccessUrl: element.documentUrl,
                                    });
                                    dataMapDocumentTypeAnalyze.set(element.id, {
                                        id: element.id,
                                        type: element.type,
                                        action: ActionRequest.Update,
                                        documentId: element.documentId,
                                        documentUrl: element.documentUrl,
                                    });
                                };
                                if (ReportDocumentType.ImgFishboneDiagram === element.type) {
                                    fileFishboneDiagramUrls.push({
                                        id: element.id,
                                        fileId: element.documentId,
                                        fileAccessUrl: element.documentUrl,
                                    });
                                    dataMapDocumentTypeFishboneDiagram.set(element.id, {
                                        id: element.id,
                                        type: element.type,
                                        action: ActionRequest.Update,
                                        documentId: element.documentId,
                                        documentUrl: element.documentUrl,
                                    });
                                }
                            } else {
                                documentBackup.push({
                                    id: element.id,
                                    type: element.type,
                                    action: ActionRequest.Update,
                                    documentId: element.documentId,
                                    documentUrl: element.documentUrl,
                                });
                            }
                        }
                    };

                    if (result.reportStatus === ReportStatus.closed) {
                        const canEdit = ((Constants.PERMISSION as any)[`${userInfo?.userProfile?.roleCode || USER_ROLE.ADMIN}`].canEdit);
                        setMode(canEdit ? Mode.Update : Mode.View);
                    } else {
                        setMode(Mode.Update);
                    }

                    let arrLevelReport = [];
                    if (!Helpers.isNullOrEmpty(result.levelReport)) {
                        const arrTemp = result.levelReport.split(",");
                        arrLevelReport = arrTemp.filter(e => !Helpers.isNullOrEmpty(e));
                        arrLevelReport = Helpers.uniqueArray(arrLevelReport);
                    };

                    const newRootCause = result.rootCause || Strings.ANALYSIS.VALUE_ROOT_CAUSE_DEFAULT;

                    setDataDocumentsBackup(documentBackup);
                    setDataMapReportDocumentTypeAnalyze(dataMapDocumentTypeAnalyze);
                    setDataMapReportDocumentTypeFishboneDiagram(dataMapDocumentTypeFishboneDiagram);

                    setDataAnalysis({
                        ...result,
                        duedate: result.duedate || undefined,
                        rootCause: newRootCause,
                        levelReport: arrLevelReport,
                        scheduleFinishDate: result.scheduleFinishDate || undefined,
                        patientIncidentCaused: result.accidentPatientCaused,
                        analyzePhotoUrls: fileAnalyzeUrls,
                        fishboneDiagramUrls: fileFishboneDiagramUrls,

                        inchargedStaffName: newDataMapUser.get(result.inchargedStaff),
                        coordinatedStaffName: newDataMapUser.get(result.coordinatedStaff),
                    });
                }
            } catch (error) {
                const err = Helpers.handleExceptionError(error);
                !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        })()
    }, [reportId, userInfo?.userProfile?.roleCode]);

    const getUserTitleByUserId = async (id: string, organizationId?: string) => {
        try {
            if (Helpers.isNullOrEmpty(id)) {
                return "";
            } else {
                let userTitle = undefined;

                const result = await userService.getDetail({ id, organizationId });

                const groupDefault = [...result?.groupUsers || []].filter(e => e.organizationId === organizationId);
                if (groupDefault) {
                    userTitle = groupDefault?.[0]?.userTitle;
                } else {
                    userTitle = "";
                }

                return userTitle;
            }
        } catch (error) {
            return "";
        }
    };

    const handleChangeValue = async (key: string, value: any) => {
        if (key) {
            let newData: any = { ...dataAnalysis };
            let newError: any = { ...errorAnalysis };

            switch (key) {
                case "appliedTreatment":
                    newData[key] = value;
                    newError[key] = undefined;
                    if (newData["recoverAction"]) newData["recoverAction"] = value;
                    newError["recoverAction"] = undefined;
                    break;
                case "inchargedStaff":
                    const valTitle1 = await getUserTitleByUserId(value?.code, dataAnalysis?.organizationId);
                    newData[key] = value?.code;
                    newError[key] = undefined;
                    newData["inchargedStaffTitle"] = valTitle1;
                    newError["inchargedStaffTitle"] = undefined;
                    newData["inchargedStaffName"] = value?.name;
                    newError["inchargedStaffName"] = undefined;
                    break;
                case "coordinatedStaff":
                    const valTitle2 = await getUserTitleByUserId(value?.code, dataAnalysis?.organizationId);
                    newData[key] = value?.code;
                    newError[key] = undefined;
                    newData["coordinatedStaffTitle"] = valTitle2;
                    newError["coordinatedStaffTitle"] = undefined;
                    newData["coordinatedStaffName"] = value?.name;
                    newError["coordinatedStaffName"] = undefined;
                    break;
                case "incidentType":
                    newData[key] = value;
                    newError[key] = undefined;
                    newData["incidentTypeDetail"] = undefined;
                    newError["incidentTypeDetail"] = undefined;
                    break;
                case "causeGroup":
                    newData[key] = value;
                    newError[key] = undefined;
                    newData["causeGroupDetail"] = undefined;
                    newError["causeGroupDetail"] = undefined;
                    break;
                case "referenceId":
                    newData["referenceId"] = value?.code;
                    newError["referenceId"] = undefined;
                    newData["referenceCode"] = value?.name;
                    newError["referenceCode"] = undefined;
                    break;
                case "repeatIssue":
                    newData[key] = value;
                    newError[key] = undefined;
                    newData["referenceId"] = undefined;
                    newError["referenceId"] = undefined;
                    newData["referenceCode"] = undefined;
                    newError["referenceCode"] = undefined;
                    break;
                default:
                    newData[key] = value;
                    newError[key] = undefined;
                    break;
            };

            setHasUpdated(true);
            setDataAnalysis(newData);
            setErrorAnalysis(newError);
        }
    };

    const handleValidate = () => {
        let isValidate = true;
        if (Helpers.isNullOrEmpty(dataAnalysis?.detailDescription)) {
            isValidate = false;
            setErrorAnalysis(prev => ({
                ...prev,
                detailDescription: Strings.ANALYSIS.PLEASE_ENTER_DETAILED_DESCRIPTION,
            }));
        }
        if (
            (dataAnalysis?.repeatIssue === RepeatIssue.RepeatedUnconfirmed) &&
            Helpers.isNullOrEmpty(dataAnalysis?.referenceId)
        ) {
            isValidate = false;
            setErrorAnalysis(prev => ({
                ...prev,
                referenceId: Strings.REPORT.PLEASE_SELECT_REFERENCE_INCIDENT,
            }));
        }
        return isValidate;
    };

    const handleSubmitAnalysis = async () => {
        if (!handleValidate()) {
            return;
        } else {
            try {

                dispatch(showLoading(true));

                let levelReports = Helpers.uniqueArray([...dataAnalysis?.levelReport || []]);

                const newReportDocuments: IReportDocument[] = [...dataDocumentsBackup || []];

                const idsPhotoAnalyze = [...dataAnalysis?.analyzePhotoUrls || []].map(e => e.id);
                for (const id of Array.from(dataMapReportDocumentTypeAnalyze.keys())) {
                    if (!idsPhotoAnalyze.includes(id)) {
                        const itemGet = dataMapReportDocumentTypeAnalyze.get(id);
                        itemGet && newReportDocuments.push({ ...itemGet, action: ActionRequest.Delete });
                    }
                }
                for (const element of [...dataAnalysis?.analyzePhotoUrls || []]) {
                    if (element.fileId && element.fileAccessUrl) {
                        const action = element.id ? ActionRequest.Update : ActionRequest.Create;
                        newReportDocuments.push({
                            id: element.id,
                            action: action,
                            documentId: element.fileId,
                            documentUrl: element.fileAccessUrl,
                            type: ReportDocumentType.ImgAnalyze,
                        });
                    }
                };

                const idsPhotoFishboneDiagram = [...dataAnalysis?.fishboneDiagramUrls || []].map(e => e.id);
                for (const id of Array.from(dataMapReportDocumentTypeFishboneDiagram.keys())) {
                    if (!idsPhotoFishboneDiagram.includes(id)) {
                        const itemGet = dataMapReportDocumentTypeFishboneDiagram.get(id);
                        itemGet && newReportDocuments.push({ ...itemGet, action: ActionRequest.Delete });
                    }
                }
                for (const element of [...dataAnalysis?.fishboneDiagramUrls || []]) {
                    if (element.fileId && element.fileAccessUrl) {
                        const action = element.id ? ActionRequest.Update : ActionRequest.Create;
                        newReportDocuments.push({
                            id: element.id,
                            action: action,
                            documentId: element.fileId,
                            documentUrl: element.fileAccessUrl,
                            type: ReportDocumentType.ImgFishboneDiagram,
                        });
                    }
                };

                const obj: IRequestReportAnalyze = {
                    id: dataAnalysis?.id || "",
                    reportStatus: dataAnalysis?.reportStatus || ReportStatus.analyzed,

                    // Analysis - General
                    recoverAction: dataAnalysis?.recoverAction,
                    appliedTreatment: dataAnalysis?.appliedTreatment,
                    suggestedSolution: dataAnalysis?.suggestedSolution,
                    detailDescription: dataAnalysis?.detailDescription || "",

                    // Analysis - Incident type by group
                    incidentType: dataAnalysis?.incidentType,
                    incidentTypeDetail: dataAnalysis?.incidentTypeDetail,

                    // Analysis - Incident type by cause
                    causeGroup: dataAnalysis?.causeGroup,
                    causeGroupDetail: dataAnalysis?.causeGroupDetail,

                    // Analysis - Expert"s comment
                    rootCause: dataAnalysis?.rootCause,
                    levelReport: levelReports.join(","),
                    detectionResult: dataAnalysis?.detectionResult,
                    detailRecommendation: dataAnalysis?.detailRecommendation,
                    fitWithRecommendRule: dataAnalysis?.fitWithRecommendRule,
                    hasSuggestedSolution: dataAnalysis?.hasSuggestedSolution,

                    // Analysis - Affection of incident
                    affectedToPatient: dataAnalysis?.affectedToPatient,
                    affectedToOrganization: dataAnalysis?.affectedToOrganization,
                    affectedToPatientCause: dataAnalysis?.affectedToPatientCause,
                    affectedToPatientCauseDetail: dataAnalysis?.affectedToPatientCauseDetail,

                    // Monitoring
                    inchargedStaff: dataAnalysis?.inchargedStaff,
                    coordinatedStaff: dataAnalysis?.coordinatedStaff,
                    inchargedStaffTitle: dataAnalysis?.inchargedStaffTitle,
                    coordinatedStaffTitle: dataAnalysis?.coordinatedStaffTitle,
                    duedate: dataAnalysis?.duedate || undefined,
                    implementPlan: dataAnalysis?.implementPlan,
                    implementResult: dataAnalysis?.implementResult,
                    scheduleFinishDate: dataAnalysis?.scheduleFinishDate || undefined,

                    // By hospital
                    byHospital: dataAnalysis?.byHospital,
                    byHospitalSource: dataAnalysis?.byHospitalSource,

                    referenceId: dataAnalysis?.referenceId,
                    repeatIssue: dataAnalysis?.repeatIssue,

                    reportDocuments: newReportDocuments,
                };

                await reportService.analyzeReport(obj);

                dispatch(setDataAlert({ message: Strings.ANALYSIS.SUBMIT_SUCCESS, type: "success" }));

                handleGoBack();

            } catch (error) {
                const err = Helpers.handleExceptionError(error);
                !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        }
    };

    const handleGoBack = () => {
        const arrPath = `${window.location.pathname}`.split("/");
        if (`/${arrPath[1]}` === Screens.REPORT) {
            const itemP = listPathName.find((el) => el.pathName === Screens.REPORT);
            navigate(Screens.REPORT + (itemP ? itemP.query : ""));
        } else if (`/${arrPath[1]}` === Screens.MONITORING) {
            const itemP = listPathName.find((el) => el.pathName === Screens.MONITORING);
            navigate(Screens.MONITORING + (itemP ? itemP.query : ""));
        } else {
            const itemP = listPathName.find((el) => el.pathName === Screens.REPORT);
            navigate(Screens.REPORT + (itemP ? itemP.query : ""));
        }
    };

    return (
        <DashboardLayout
            isPermission
            requiredOrganization
            title={Strings.ANALYSIS.REPORT_ANALYSIS}
            route={[
                { title: Strings.REPORT.LIST_REPORT, route: Screens.REPORT },
                { title: Strings.ANALYSIS.REPORT_ANALYSIS, route: "" },
            ]}
            listActions={[
                {
                    color: "secondary",
                    onPress: handleGoBack,
                    title: Strings.COMMON.BACK,
                },
                {
                    title: Strings.COMMON.SAVE,
                    onPress: handleSubmitAnalysis,
                },
            ]}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <CardGeneralInformation
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardIncidentGroup
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            incidentType: [...dataMapCodenames?.incidentType || []],
                            medicineCaused: [...dataMapCodenames?.medicineCaused || []],
                            infectionCaused: [...dataMapCodenames?.infectionCaused || []],
                            misbehaveCaused: [...dataMapCodenames?.misbehaveCaused || []],
                            procedureCaused: [...dataMapCodenames?.procedureCaused || []],
                            managementCaused: [...dataMapCodenames?.managementCaused || []],
                            bloodProductCaused: [...dataMapCodenames?.bloodProductCaused || []],
                            infrastructureCaused: [...dataMapCodenames?.infrastructureCaused || []],
                            patientIncidentCaused: [...dataMapCodenames?.patientIncidentCaused || []],
                            technicalProcessCaused: [...dataMapCodenames?.technicalProcessCaused || []],
                            medicalEquipmentCaused: [...dataMapCodenames?.medicalEquipmentCaused || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardByHospital
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            byHospital: [...dataMapCodenames?.byHospital || []],
                            byHospitalSources: [...dataMapCodenames?.byHospitalSource || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardCause
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            causeGroup: [...dataMapCodenames?.causeGroup || []],
                            staffCaused: [...dataMapCodenames?.staffCaused || []],
                            patientCaused: [...dataMapCodenames?.patientCaused || []],
                            serviceCaused: [...dataMapCodenames?.serviceCaused || []],
                            environmentCaused: [...dataMapCodenames?.environmentCaused || []],
                            externalFactorCaused: [...dataMapCodenames?.externalFactorCaused || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardExpertEvaluation
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            common: [...dataMapCodenames?.common || []],
                            levelReport: [...dataMapCodenames?.levelReport || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardSeverityOfInjury
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            crime: [...dataMapCodenames?.crime || []],
                            medical: [...dataMapCodenames?.medical || []],
                            surgery: [...dataMapCodenames?.surgery || []],
                            equipment: [...dataMapCodenames?.equipment || []],
                            affectedToPatient: [...dataMapCodenames?.affectedToPatient || []],
                            managementPatient: [...dataMapCodenames?.managementPatient || []],
                            environmentPatient: [...dataMapCodenames?.environmentPatient || []],
                            affectedToOrganization: [...dataMapCodenames?.affectedToOrganization || []],
                            affectedToPatientCause: [...dataMapCodenames?.affectedToPatientCause || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardIncidentResolutionMonitoring
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        errorAnalysis={errorAnalysis}
                        handleChangeValue={handleChangeValue}
                        dataOptions={{
                            reportStatus: [...dataMapCodenames?.reportStatus || []],
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardImageFishboneDiagrams
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        handleChangeValue={handleChangeValue}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CardImageAnalysis
                        mode={mode}
                        dataAnalysis={dataAnalysis}
                        handleChangeValue={handleChangeValue}
                    />
                </Grid>
            </Grid>
        </DashboardLayout>
    );
};

export default ReportAnalysisScreen;
